import * as Sentry from '@sentry/vue';

const FAMILY = 'Font Awesome 6 Pro';

// Font files are served `immutable` (see nginx/nginx.conf), so once the browser holds a broken
// copy of one, every icon of that weight stays invisible and only a hard reload brings it back --
// a normal reload never revalidates subresources. This guard notices a FontAwesome face that
// failed to load, re-downloads it past the HTTP cache (`cache: 'reload'` also overwrites the bad
// entry, so later page loads are fixed too), registers the fresh copy, and reports what the broken
// cached copy looked like so the root cause can be pinned down.
//
// A face can also hang in `loading` forever (seen sporadically in Chrome after switching project
// subdomains, even though the font bytes arrive intact). That never fires `loadingerror` and never
// resolves `document.fonts.ready`, so faces still loading after STUCK_MS are treated as broken too.
// Chrome keeps matching the hung CSS face over an added duplicate, so the @font-face rule itself is
// removed once the fresh copy is registered.

const STUCK_MS = 10000;
const FETCH_TIMEOUT_MS = 15000;

const healed = new Set();
const loadingSince = new WeakMap();

const isIconFace = (face) => face.family.replace(/["']/g, '') === FAMILY;

const findFontRules = (weight) => {
  const found = [];
  [...document.styleSheets].forEach((sheet) => {
    let rules = [];
    try {
      rules = [...sheet.cssRules];
    } catch (e) {
      // Cross-origin stylesheet, cannot contain our @font-face
    }
    rules.forEach((rule) => {
      if (rule instanceof CSSFontFaceRule
        && rule.style.fontFamily.replace(/["'\\]/g, '') === FAMILY
        && rule.style.fontWeight === weight) found.push({ sheet, rule });
    });
  });
  return found;
};

const findFontUrl = (rules) => {
  for (let i = 0; i < rules.length; i += 1) {
    const { sheet, rule } = rules[i];
    const match = rule.style.getPropertyValue('src').match(/url\(["']?([^"')]+\.woff2)["']?\)/);
    if (match) return new URL(match[1], sheet.href || window.location.href).href;
  }
  return null;
};

// Disconnects the CSS-connected face(s) so only the fresh FontFace matches this weight
const removeFontRules = (rules) => {
  let removed = 0;
  rules.forEach(({ sheet, rule }) => {
    const index = [...sheet.cssRules].indexOf(rule);
    if (index === -1) return;
    try {
      sheet.deleteRule(index);
      removed += 1;
    } catch (e) {
      // Leave the rule; the report shows `rulesRemoved` falling short
    }
  });
  return removed;
};

const describeCachedCopy = async (url) => {
  try {
    const response = await fetch(url, { cache: 'only-if-cached', mode: 'same-origin' });
    const bytes = new Uint8Array(await response.arrayBuffer());
    return {
      status: response.status,
      contentType: response.headers.get('content-type'),
      cacheControl: response.headers.get('cache-control'),
      etag: response.headers.get('etag'),
      length: bytes.length,
      magic: String.fromCharCode(...bytes.slice(0, 4)),
    };
  } catch (e) {
    return { notCached: true };
  }
};

// Plugins install before Sentry.init (main.js waits for config.json first)
const report = (extra, attempt = 0) => {
  // Also log it: Sentry ingest is commonly blocked by ad blockers
  if (attempt === 0) console.warn('fa-font-load-error', extra); // eslint-disable-line no-console
  if (Sentry.getClient()) {
    Sentry.captureMessage('fa-font-load-error', { level: 'warning', extra });
  } else if (attempt < 10) {
    setTimeout(() => report(extra, attempt + 1), 2000);
  }
};

const faceStatuses = () => {
  const statuses = {};
  document.fonts.forEach((face) => {
    if (isIconFace(face)) statuses[face.weight] = face.status;
  });
  return statuses;
};

const heal = async (face, reason) => {
  const { weight } = face;
  if (healed.has(weight)) return;
  healed.add(weight);

  const base = {
    weight,
    reason,
    facesAfter: faceStatuses(),
    host: window.location.host,
    referrer: document.referrer,
  };

  const rules = findFontRules(weight);
  const url = findFontUrl(rules);
  if (!url) {
    report({ ...base, healed: false, noUrl: true });
    return;
  }

  const cached = await describeCachedCopy(url);
  let healedOk = false;
  let rulesRemoved = 0;
  let error = null;
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller && setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(url, { cache: 'reload', signal: controller && controller.signal });
    const fresh = new FontFace(FAMILY, await response.arrayBuffer(), {
      weight,
      style: 'normal',
      display: 'block',
    });
    await fresh.load();
    document.fonts.add(fresh);
    rulesRemoved = removeFontRules(rules);
    healedOk = document.fonts.check(`${weight} 1em "${FAMILY}"`);
  } catch (e) {
    error = String(e);
  } finally {
    clearTimeout(timer);
  }

  report({
    ...base,
    url,
    cached,
    healed: healedOk,
    rulesRemoved,
    facesAfter: faceStatuses(),
    error,
  });
};

let checkTimer = null;

const checkFaces = () => {
  const now = Date.now();
  let stillLoading = false;
  document.fonts.forEach((face) => {
    if (!isIconFace(face)) return;
    if (face.status === 'error') {
      heal(face, 'error').catch(() => {});
    } else if (face.status === 'loading') {
      if (!loadingSince.has(face)) loadingSince.set(face, now);
      if (now - loadingSince.get(face) >= STUCK_MS) {
        heal(face, 'stuck-loading').catch(() => {});
      } else {
        stillLoading = true;
      }
    } else {
      loadingSince.delete(face);
    }
  });
  if (stillLoading) scheduleCheck(); // eslint-disable-line no-use-before-define
};

const scheduleCheck = () => {
  if (checkTimer) return;
  checkTimer = setTimeout(() => {
    checkTimer = null;
    checkFaces();
  }, STUCK_MS / 2);
};

const iconFontGuard = {
  install() {
    if (!document.fonts || typeof FontFace === 'undefined') return;

    document.fonts.addEventListener('loadingerror', (event) => {
      event.fontfaces.filter(isIconFace).forEach((face) => heal(face, 'error').catch(() => {}));
    });
    // `ready` never resolves while a face hangs, so poll whenever loading starts
    document.fonts.addEventListener('loading', () => {
      checkFaces();
      scheduleCheck();
    });
    document.fonts.ready.then(checkFaces).catch(() => {});
    checkFaces();
    scheduleCheck();
  },
};

export default iconFontGuard;
